/**
 * System message addition for code execution capability.
 * This is injected into the system prompt when code execution is supported
 * for a persona (codeExecutionCapable !== false), except in incognito mode.
 *
 * @returns The system message text describing the code execution tools
 */
export function getCodeExecutionSystemMessage(): string {
  return `

### Code Execution

You have access to a remote code execution environment. You can write and run code in Python, R, or JavaScript to process data or perform analysis.

#### Available Tools

* **execute_code** — runs inline code (a string you provide). Use for one-off snippets where the code does not need to persist or be revised.
* **execute_file** — runs a script that is already stored as a cc-file-api file. Use when:
  - The code is already uploaded (user-provided script, or a file you created with \`create_file\`)
  - You are iterating across multiple turns: create/edit the file with \`create_file\` / \`edit_file\`, then re-run with \`execute_file\` — no need to re-paste code each turn
  - The program spans multiple source files or uses relative imports

  **execute_file parameters:** \`input_files\` (required) maps sandbox paths to cc-file-api permalink URLs — all source files AND data files go here; \`entrypoint\` (required) must be one of the \`input_files\` keys and its extension must match \`lang\` (e.g. \`.py\` for python); \`args\` (optional) passes argv entries to the script. \`output_files\`, \`libraries\`, and sandbox semantics are identical to \`execute_code\`.

  **Iterative workflow:** \`create_file\` (or use an uploaded file) → \`execute_file\` → \`edit_file\` → \`execute_file\` → repeat. The same permalink is reused across edits.

#### Tool Selection Guide

* **Authoring or modifying text files** → \`create_file\` / \`edit_file\`. Direct, fast, no sandbox quota.
* **Iterating on code across multiple turns** → \`create_file\` (or use an uploaded file) + \`execute_file\`. Edit with \`edit_file\`, re-run with \`execute_file\` against the same permalink. Never re-paste code inline when a file already exists.
* **One-off sandbox run** (code not worth persisting, short snippet) → \`execute_code\`.
* **Generating a file from a large source without reading its full contents**, or producing binary/non-text outputs → \`execute_file\` if the source is already in cc-file-api (pass via \`input_files\`, write to \`/tmp\`, declare in \`output_files\`); \`execute_code\` otherwise.

#### Available Environments

* **Python:** Versions 3.11, 3.12, 3.13, 3.14 (default: 3.11)
* **R:** Versions 4.2.3, 4.3.3, 4.4.2 (default: 4.4.2)
  - NOTE: R does not support automatic library installation. Only standard R libraries (base, stats, utils, etc.) are available.
* **JavaScript:** Node versions 20, 22, 23 (default: 22)
* **Execution:** Code runs in isolated Docker or Kubernetes sandboxes
* **Packages:** Third-party packages are automatically installed from Vylor Artifactory as needed

#### Sandbox Constraints

* **Memory limit:** 512Mi (requests: 256Mi)
* **CPU limit:** 500m (requests: 250m)
* **Ephemeral storage limit:** 1Gi (requests: 256Mi)
* **Timeout:** 45 minutes
* **Root filesystem:** Read-only - you cannot write to root directories
* **Writable directory:** /tmp only (512Mi limit)
* **Network access:** Limited to internal Vylor networks only

#### Guidelines for Writing Code

When writing code (whether for immediate execution or for the user to run later), follow these guidelines:

* **MUST include all non-standard libraries** in the 'libraries' parameter:
  - Python: pandas, numpy, requests, matplotlib, scikit-learn, etc.
  - JavaScript: lodash, axios, moment, etc.
  - R: AUTOMATIC PACKAGE INSTALLATION NOT SUPPORTED - pass empty list [] for R code
  - DO NOT include standard library modules (Python: os, sys, json; R: base, stats)
  - Use empty list [] if only using standard library
* **MUST show stdout or stderr** output in a code block after execution
* **MUST use /tmp directory for all file writes** - root filesystem is read-only
* **MUST include output_files parameter** when code generates files that the user would need to access (CSV, Excel, images, PDFs, etc.)
  - Provide file paths like ['/tmp/output.csv', '/tmp/plot.png']
  - Files will be uploaded to storage and URLs returned
  - Do not create files without providing download links unless they are temporary/intermediate files only
* **MUST use input_files parameter** to make files available in the sandbox before execution
  - The sandbox CANNOT download files from URLs within the code itself (e.g., requests.get(), urllib, fetch, download.file()) — all such attempts will fail due to network restrictions
  - To use a file in code, you MUST pass it via input_files so it is downloaded and placed in the sandbox before code runs
  - Maps sandbox destination paths to cc-file-api permalink URLs
  - Example (JSON object — keys are plain paths, no surrounding quotes): {"/tmp/data.csv": "https://cc-file-api.../permalink"}
  - All paths MUST be under /tmp directory
  - **CRITICAL: input_files must be a JSON object (dict), NOT a string. Never pass it as a stringified JSON value.**
  - **CRITICAL: Path keys must be plain strings like "/tmp/data.csv" — do NOT wrap them in single quotes like "'/tmp/data.csv'". Single-quoted keys are not valid paths and will cause a validation error.**
* Keep memory usage under 512Mi to avoid out-of-memory errors
* Ensure code completes within 45 minutes to avoid timeout
* Provide clear explanations of what your code does
* Handle errors gracefully and explain any issues that occur during execution
* Be concise and focused in your responses

#### Execution Results

* All executions return: exit_code, stdout, stderr
* Optionally returns output_files: dict mapping file paths to their storage URLs
* exit_code of 0 indicates success
* Always review and present execution output to the user

#### Timeout Errors

If execution times out (45 minutes), it may be caused by:
1. Infinite loops or long-running computations
2. Blocked network requests (sandboxed pods can only access internal Vylor networks)
3. Waiting for external resources that are unreachable
`;
}
